import { useState, useCallback, useEffect, useRef } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface DomainSearchInputProps {
  onSearch: (query: string) => void;
  isSearching: boolean;
}

const DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;

export function DomainSearchInput({ onSearch, isSearching }: DomainSearchInputProps) {
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, '');
    setInputValue(value);
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmed = inputValue.trim();
    if (trimmed.length >= MIN_SEARCH_LENGTH) {
      debounceRef.current = setTimeout(() => {
        onSearch(trimmed);
      }, DEBOUNCE_MS);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, onSearch]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      onSearch(inputValue.trim());
    }
  }, [inputValue, onSearch]);

  return (
    <div className="relative">
      <Input
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Search for a domain (e.g., mycompany.com)"
        prefixIcon={<Search className="h-5 w-5" />}
        className="w-full mb-0 pr-12"
      />
      {isSearching && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
    </div>
  );
}
